import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {TextFieldStyled} from "../form/FormField";
import React, {useRef, useState} from "react";
import CustomButton from "../ui/CustomButton";
import Icon from "../ui/Icon";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FiltersRenderField from "./FiltersRenderField";
import {TableColumnsWithFilter} from "./_interfaces";

interface FilterFormForMultipleColumnsProps {
    columns: TableColumnsWithFilter[];
    values: Record<string, any>;
    onChange: (filterType: string, columnId: string, value: any) => void;
    onCancel: () => void;
    onApply: () => void;
    resetFormData?: () => void;
    activeColumnIds: string[];
    onActiveColumnIdsChange: (ids: string[]) => void;
    filterOptions?: Record<string, any>;
}

const FilterFormForMultipleColumns = ({
                                          columns,
                                          values,
                                          onChange,
                                          onCancel,
                                          onApply,
                                          resetFormData,
                                          activeColumnIds,
                                          onActiveColumnIdsChange,
                                          filterOptions = {}
                                      }: FilterFormForMultipleColumnsProps) => {
    const columnsToFilter = columns.filter(column => column.filter === true);
    const [selectedColumnId, setSelectedColumnId] = useState<string | "">("");
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const handleAddColumn = (e: React.ChangeEvent<HTMLInputElement>) => {
        const columnId = e.target.value;
        if (!columnId) return;

        if (!activeColumnIds.includes(columnId)) {
            onActiveColumnIdsChange([...activeColumnIds, columnId]);
        }
        setSelectedColumnId("");
    };

    const handleOnChange = (filterType: string, columnId: string, value: any) => {
        onChange(filterType, columnId, value);
        if (filterType === "dateRange") {
            if (value.length === 2 && value[0] !== null && value[1] !== null) {
                onApply();
            }
        } else if (filterType === "number") {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                onApply();
            }, 600);
        } else {
            onApply();
        }
    }

    const handleColumnReset = (filterType: string, columnId: string) => {
        if (activeColumnIds.includes(columnId)) {
            onActiveColumnIdsChange(activeColumnIds.filter(id => id !== columnId));
            onChange(filterType, columnId, "");
            onApply();
        }
    };

    const handleReset = () => {
        const defaultValueColumn = columns.find(column => column.hasDefaultValue === true);
        if (defaultValueColumn && activeColumnIds.includes(defaultValueColumn.id)) {
            const newActiveColumnIds = activeColumnIds.filter(id => id === defaultValueColumn.id);
            onActiveColumnIdsChange(newActiveColumnIds);
        } else {
            setSelectedColumnId("");
            onActiveColumnIdsChange([]);
        }

        if (resetFormData) {
            resetFormData();
        }
    };

    return (
        <Box>
            <DialogTitle display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{m: 0, p: 2}}>
                <Box flexGrow={1}>
                    <Typography
                        variant="h3"
                        color="info.contrastText"
                    >Adaugă filtre</Typography>
                </Box>
                <CustomButton
                    buttonType="icon"
                    submit={true}
                    appearance="circle"
                    bgcolor="white"
                    width={52}
                    height={52}
                    onClick={onCancel}
                    icon={<Icon icon="fal xmark" sx={{color: "primary.main"}}/>}
                    sxExtra={{
                        color: "secondary.main",
                        backgroundColor: "transparent",
                        padding: "8",
                        "&:hover": {
                            color: "secondary.main",
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                        "&[aria-expanded='true']": {
                            backgroundColor: "white",
                        },
                        "&.Mui-disabled": {
                            border: "none",
                        },
                    }}
                />
            </DialogTitle>
            <DialogContent sx={(theme) => ({m: 0, p: {xs: theme.spacing(1, 1, 2, 1), md:theme.spacing(2)}, overflow: "visible"})}>
                <Box sx={(theme) => ({
                    border: `${theme.spacing(0.1)} solid #ddd`,
                    borderRadius: theme.spacing(1.6),
                    marginBottom: {xs: theme.spacing(1), sm: theme.spacing(1.5)}
                })}>
                    <TextFieldStyled
                        name="column"
                        SelectProps={{
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        backgroundColor: "primary.light",
                                        borderRadius: "15px",
                                    },
                                },
                                BackdropProps: {
                                    invisible: true,
                                },
                            },
                        }}
                        fullWidth
                        select
                        margin="normal"
                        label="Alege coloana"
                        value={selectedColumnId}
                        onChange={handleAddColumn}
                    >
                        {columnsToFilter.map((option) =>
                            <MenuItem key={option.id} value={option.id} disabled={activeColumnIds.includes(option.id)}>
                                {option.label}
                            </MenuItem>
                        )}
                    </TextFieldStyled>
                </Box>
                {activeColumnIds.length > 0 && (
                    <>
                        {activeColumnIds.map((columnId) => {
                            const column = columnsToFilter.find((c) => c.id === columnId);
                            if (!column) return null;

                            return (
                                <FiltersRenderField key={columnId} column={column} values={values} onChange={handleOnChange} handleReset={handleColumnReset} filterOptions={filterOptions}/>
                            );
                        })}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={(theme) => ({m: 0, p: theme.spacing(0, 2, 3, 2), mt: -1})}>
                <Box
                    display="flex"
                    gap={2}
                    width="100%"
                    alignItems="center"
                    justifyContent="space-between"
                    mt={2}
                >
                    <CustomButton
                        buttonType="textIconOutlined"
                        onClick={onCancel}
                        color="info.contrastText"
                        borderColor="info.contrastText"
                        bgcolor="modal"
                    >
                        Închide
                    </CustomButton>
                    <CustomButton
                        buttonType="textIcon"
                        onClick={handleReset}
                        bgcolor="modal"
                    >
                        Resetează
                    </CustomButton>
                </Box>
            </DialogActions>
        </Box>
    );
};

export default FilterFormForMultipleColumns